import { styled, css } from '@openeducation/oe-rn-themes';
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  LayoutChangeEvent,
  StyleProp,
  ViewStyle,
} from 'react-native';

import { ProgressBarPopup } from './components';

type ViewStyleType = StyleProp<ViewStyle>;
export interface ProgressBarProps {
  percentage: number;
  showPopup?: boolean;
  style?: ViewStyleType;
  barWrapperStyle?: ViewStyleType;
  barStyle?: ViewStyleType;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  percentage,
  showPopup = true,
  style,
  barWrapperStyle,
  barStyle,
}) => {
  const [width, setWidth] = useState(0);
  const animation = useRef(new Animated.Value(0));

  const handleProgressBarLayout = (event: LayoutChangeEvent) => {
    event?.persist?.();
    setWidth(event?.nativeEvent?.layout?.width);
  };

  useEffect(() => {
    if (isNaN(percentage)) {
      return;
    }

    Animated.sequence([
      Animated.delay(300),
      Animated.timing(animation.current, {
        duration: 400,
        toValue: Math.min(percentage, 1),
        useNativeDriver: false,
      }),
    ]).start();
  }, [percentage]);

  return (
    <Container style={style}>
      {showPopup && (
        <PopupContainer>
          <ProgressBarPopup barWidth={width} percentage={percentage} />
        </PopupContainer>
      )}
      <BarWrapper onLayout={handleProgressBarLayout} style={[barWrapperStyle]}>
        <Bar
          style={[
            {
              width: animation.current.interpolate({
                inputRange: [0, 1],
                outputRange: ['0%', '100%'],
                extrapolate: 'clamp',
              }),
            },
            barStyle,
          ]}
        />
      </BarWrapper>
    </Container>
  );
};

const Container = styled.View`
  flex: 1;
  width: 100%;
`;

const PopupContainer = styled.View`
  position: absolute;
  z-index: 9;
  top: -30px;
`;

const barStyles = css`
  height: 4px;
  border-radius: 2px;
`;

const BarWrapper = styled.View`
  ${barStyles};
  width: 100%;
  background-color: ${({ theme }) => theme.color.lightPrimary};
`;

const Bar = styled(Animated.View)`
  ${barStyles};
  background-color: ${({ theme }) => theme.color.primary};
`;
